// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconMemo, IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyPicture from "@rpsc/MobiusOverrides/LazyImage/LazyPicture";
import IconTag from "@rpsc/XDL/Icons/IconTag";
import { xParserOptions } from "@rpsc/XDL/Utilities";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    background = "background",
    image = "image",
    backgroundColor = "backgroundColor",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    hideButton = "hideButton",
    customCSS = "customCSS",
    imagePosition = "imagePosition",
    imageAltText = "imageAltText",
    openInNewWindow = "openInNewWindow",
    desktopImage = "desktopImage",
    tabletImage = "tabletImage",
    mobileImage = "mobileImage",
    showTopicTags = "showTopicTags",
    topics = "topics",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary";
        [fields.hideButton]: boolean;
        [fields.customCSS]: string;
        [fields.imagePosition]: "right" | "left";
        [fields.imageAltText]: string;
        [fields.openInNewWindow]: boolean;
        [fields.desktopImage]: string;
        [fields.tabletImage]: string;
        [fields.mobileImage]: string;
        [fields.showTopicTags]: boolean;
        [fields.topics]: {
            fields: {
                topic: string;
                topicUrl: LinkFieldValue;
            };
        }[];
    };
}

export interface InfoCardStyles {
    wrapper?: InjectableCss;
    infoCardButton?: ButtonPresentationProps;
    heading?: TypographyProps;
    subHeading?: TypographyProps;
    imageWrapperStyles?: InjectableCss;
    textWrapperStyles?: InjectableCss;
    content?: InjectableCss;
    topicTagsWrapper?: InjectableCss;
    tagIconStyles?: IconProps;
    topicLinksWrapper?: InjectableCss;
    topicTagLink?: LinkPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {};

const styles = infoCardStyles;

export const InfoCard: FC<OwnProps> = ({
    fields: {
        backgroundColor,
        buttonLabel,
        buttonLink,
        heading,
        hideButton,
        desktopImage,
        mobileImage,
        tabletImage,
        imageAltText,
        openInNewWindow,
        subheading,
        variant,
        showTopicTags,
        topics,
        customCSS,
    },
}) => {
    const { url, title } = useGetLink(buttonLink);
    const topicLinks = !!topics && useGetLinks(topics, l => l.fields.topicUrl);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            openInNewWindow ? window.open(url) : (window.location.href = url);
        } else {
            openInNewWindow ? window.open(url) : history.push(url);
        }
    };

    const customCssWrapper = {
        css: css`
            height: 100%;
            ${customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.wrapper} className="wrapper">
                {!!tabletImage && (
                    <StyledWrapper {...styles.imageWrapperStyles} className="image">
                        <LazyPicture
                            smallImageSrc={mobileImage || tabletImage}
                            mediumImageSrc={tabletImage}
                            largeImageSrc={desktopImage || tabletImage}
                            altText={imageAltText}
                        />
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.content} className="content-wrapper">
                    <StyledWrapper {...styles.textWrapperStyles} {...{ backgroundColor }}>
                        {heading && (
                            <Typography {...styles.heading} {...{ showTopicTags }} className="heading-text">
                                {parse(heading, xParserOptions)}
                            </Typography>
                        )}

                        {subheading && (
                            <Typography {...styles.subHeading} {...{ showTopicTags }} className="subHeading-text">
                                {parse(subheading, xParserOptions)}
                            </Typography>
                        )}
                    </StyledWrapper>
                    {showTopicTags && !!topics.length && (
                        <StyledWrapper {...styles.topicTagsWrapper}>
                            <IconMemo {...styles.tagIconStyles} src={IconTag} />
                            <StyledWrapper {...styles.topicLinksWrapper}>
                                {topics?.map((item, index) => {
                                    const topicLink = topicLinks[index].url;
                                    return (
                                        <React.Fragment key={item.fields.topic}>
                                            <Link
                                                {...styles.topicTagLink}
                                                onClick={() => onClick(topicLink)}
                                                aria-label={item.fields.topic}
                                            >
                                                {item.fields.topic}
                                                {index < topics.length - 1 && ","}
                                            </Link>
                                        </React.Fragment>
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    {!hideButton && (buttonLabel || title || url) && (
                        <Button
                            {...styles.infoCardButton}
                            variant={variant}
                            onClick={() => onClick(url)}
                            aria-label={buttonLabel || title || url}
                            {...{ showTopicTags }}
                        >
                            {buttonLabel || title || url}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const defaultCustomCss = `.wrapper {
}

.image {
}

.content-wrapper {
}

.heading-text {
}

.subHeading-text {
}`;

export const fieldDefinitions = [
    {
        fieldType: "Contextual",
        name: fields.desktopImage,
        displayName: "Large Image",
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.tabletImage,
        displayName: "Medium Image",
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.mobileImage,
        displayName: "Small Image",
        editorTemplate: "ImagePickerField",
        defaultValue: "",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.imageAltText,
        editorTemplate: "TextField",
        displayName: "Image Alt Text",
        defaultValue: "",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.backgroundColor,
        editorTemplate: "ColorPickerField",
        displayName: "Background Color",
        defaultValue: "",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.heading,
        editorTemplate: "RichTextField",
        displayName: "Title",
        defaultValue: "Title",
        extendedConfig: { height: 100 },
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.subheading,
        editorTemplate: "RichTextField",
        displayName: "Subheading",
        defaultValue: "Subheading",
        extendedConfig: { height: 170 },
        tab: basicTab,
    },
    {
        name: fields.buttonVariant,
        displayName: "Button Type",
        editorTemplate: "DropDownField",
        options: [
            { displayName: "Primary", value: "primary" },
            { displayName: "Secondary", value: "secondary" },
        ],
        hideEmptyOption: true,
        defaultValue: "primary",
        fieldType: "Contextual",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.buttonLabel,
        editorTemplate: "TextField",
        displayName: "Button Label",
        defaultValue: "Label",
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.buttonLink,
        displayName: "Button URL",
        editorTemplate: "LinkField",
        defaultValue: { type: "Page", value: "" },
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.openInNewWindow,
        editorTemplate: "CheckboxField",
        defaultValue: false,
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.hideButton,
        editorTemplate: "CheckboxField",
        defaultValue: false,
        tab: basicTab,
    },
    {
        fieldType: "Contextual",
        name: fields.showTopicTags,
        editorTemplate: "CheckboxField",
        defaultValue: false,
        tab: basicTab,
    },
    {
        name: fields.topics,
        editorTemplate: "ListField",
        fieldType: "General",
        defaultValue: [],
        getDisplay: (item: HasFields) => {
            return item.fields.topic;
        },
        isVisible: (item: HasFields) => item.fields.showTopicTags,
        fieldDefinitions: [
            {
                name: "topic",
                displayName: "topic",
                editorTemplate: "SystemListDropDownField",
                systemListName: "Topics",
                defaultValue: "",
            },
            {
                name: "topicUrl",
                displayName: "Topic URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
        ],
    },
];

const infoCard: WidgetModule = {
    component: InfoCard,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            ...fieldDefinitions,
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item: any, shouldDisplayAdvancedFeatures: any) => !!shouldDisplayAdvancedFeatures,
                validate: (value: any) => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            } as any,
        ],
    },
};

export default infoCard;
